interface Coordinates {
  latitude: number;
  longitude: number;
}

interface LocationData {
  coordinates: Coordinates;
  address: string;
  city: string;
  country: string;
  accuracy: number;
}

interface DistanceResult {
  distance: number; // in kilometers
  duration: number; // in minutes (estimated)
}

class GeolocationService {
  private watchId: number | null = null;
  private currentLocation: LocationData | null = null;

  // Get current position
  async getCurrentPosition(): Promise<LocationData> {
    return new Promise((resolve, reject) => {
      if (!navigator.geolocation) {
        reject(new Error('Geolocation is not supported by this browser'));
        return;
      }

      const options: PositionOptions = {
        enableHighAccuracy: true,
        timeout: 10000,
        maximumAge: 300000 // 5 minutes
      };

      navigator.geolocation.getCurrentPosition(
        async (position) => {
          try {
            const locationData = await this.processPosition(position);
            this.currentLocation = locationData;
            resolve(locationData);
          } catch (error) {
            reject(error);
          }
        },
        (error) => {
          reject(this.handleGeolocationError(error));
        },
        options
      );
    });
  }

  // Watch position changes
  watchPosition(callback: (location: LocationData) => void): number {
    if (!navigator.geolocation) {
      throw new Error('Geolocation is not supported');
    }

    const options: PositionOptions = {
      enableHighAccuracy: true,
      timeout: 10000,
      maximumAge: 60000 // 1 minute
    };

    this.watchId = navigator.geolocation.watchPosition(
      async (position) => {
        try {
          const locationData = await this.processPosition(position);
          this.currentLocation = locationData;
          callback(locationData);
        } catch (error) {
          console.error('Error processing position:', error);
        }
      },
      (error) => {
        console.error('Geolocation error:', this.handleGeolocationError(error));
      },
      options
    );

    return this.watchId;
  }

  // Stop watching position
  stopWatching(): void {
    if (this.watchId !== null) {
      navigator.geolocation.clearWatch(this.watchId);
      this.watchId = null;
    }
  }

  // Calculate distance between two points
  calculateDistance(
    point1: Coordinates,
    point2: Coordinates
  ): number {
    const R = 6371; // Earth's radius in kilometers
    const dLat = this.toRadians(point2.latitude - point1.latitude);
    const dLon = this.toRadians(point2.longitude - point1.longitude);
    
    const a = 
      Math.sin(dLat / 2) * Math.sin(dLat / 2) +
      Math.cos(this.toRadians(point1.latitude)) * 
      Math.cos(this.toRadians(point2.latitude)) * 
      Math.sin(dLon / 2) * Math.sin(dLon / 2);
    
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
    return R * c;
  }

  // Get distance and estimated travel time
  getDistanceAndDuration(
    from: Coordinates,
    to: Coordinates
  ): DistanceResult {
    const distance = this.calculateDistance(from, to);
    
    // Estimate duration based on distance (assuming average city speed)
    let duration: number;
    if (distance <= 5) {
      duration = distance * 8; // 8 minutes per km for short distances
    } else if (distance <= 20) {
      duration = distance * 6; // 6 minutes per km for medium distances
    } else {
      duration = distance * 4; // 4 minutes per km for longer distances
    }

    return {
      distance: Math.round(distance * 10) / 10, // Round to 1 decimal
      duration: Math.round(duration)
    };
  }

  // Find nearby providers
  findNearbyProviders(
    userLocation: Coordinates,
    providers: Array<{ id: string; location: string; coordinates?: Coordinates }>,
    maxDistance: number = 25 // km
  ): Array<{ id: string; distance: number; duration: number }> {
    return providers
      .map(provider => {
        // For demo, generate coordinates based on location string
        const coordinates = provider.coordinates || this.getCoordinatesFromLocation(provider.location);
        const result = this.getDistanceAndDuration(userLocation, coordinates);
        
        return {
          id: provider.id,
          distance: result.distance,
          duration: result.duration
        };
      })
      .filter(result => result.distance <= maxDistance)
      .sort((a, b) => a.distance - b.distance);
  }

  // Reverse geocoding (coordinates to address)
  async reverseGeocode(coordinates: Coordinates): Promise<string> {
    // In a real app, you'd use Google Maps API or similar
    // For demo, return mock address based on coordinates
    const addresses = [
      'Dublin 2, Ireland',
      'Dublin 4, Ireland', 
      'Dublin 1, Ireland',
      'Dublin 8, Ireland',
      'Dublin 6, Ireland'
    ];
    
    // Simple mock based on coordinates
    const index = Math.floor(Math.abs(coordinates.latitude + coordinates.longitude) * 100) % addresses.length;
    return addresses[index];
  }

  // Forward geocoding (address to coordinates)
  async geocodeAddress(address: string): Promise<Coordinates> {
    // Mock geocoding for demo
    const mockCoordinates: Record<string, Coordinates> = {
      'Dublin 1': { latitude: 53.3498, longitude: -6.2603 },
      'Dublin 2': { latitude: 53.3381, longitude: -6.2592 },
      'Dublin 4': { latitude: 53.3321, longitude: -6.2419 },
      'Dublin 6': { latitude: 53.3147, longitude: -6.2622 },
      'Dublin 8': { latitude: 53.3439, longitude: -6.2924 }
    };

    // Find best match
    const normalizedAddress = address.toLowerCase();
    for (const [key, coords] of Object.entries(mockCoordinates)) {
      if (normalizedAddress.includes(key.toLowerCase())) {
        return coords;
      }
    }

    // Default to Dublin city center
    return { latitude: 53.3498, longitude: -6.2603 };
  }

  // Get current location or return cached
  getCurrentLocationCached(): LocationData | null {
    return this.currentLocation;
  }

  // Check if location services are available
  isGeolocationSupported(): boolean {
    return 'geolocation' in navigator;
  }

  // Request location permission
  async requestLocationPermission(): Promise<boolean> {
    if (!this.isGeolocationSupported()) {
      return false;
    }

    try {
      await this.getCurrentPosition();
      return true;
    } catch (error) {
      console.error('Location permission denied:', error);
      return false;
    }
  }

  private async processPosition(position: GeolocationPosition): Promise<LocationData> {
    const coordinates: Coordinates = {
      latitude: position.coords.latitude,
      longitude: position.coords.longitude
    };

    const address = await this.reverseGeocode(coordinates);
    
    return {
      coordinates,
      address,
      city: this.extractCity(address),
      country: 'Ireland', // Default for demo
      accuracy: position.coords.accuracy
    };
  }

  private handleGeolocationError(error: GeolocationPositionError): Error {
    switch (error.code) {
      case error.PERMISSION_DENIED:
        return new Error('Location access denied by user');
      case error.POSITION_UNAVAILABLE:
        return new Error('Location information unavailable');
      case error.TIMEOUT:
        return new Error('Location request timed out');
      default:
        return new Error('Unknown location error');
    }
  }

  private toRadians(degrees: number): number {
    return degrees * (Math.PI / 180);
  }

  private extractCity(address: string): string {
    // Simple city extraction for demo
    if (address.includes('Dublin')) return 'Dublin';
    return 'Unknown';
  }

  private getCoordinatesFromLocation(location: string): Coordinates {
    // Mock coordinates for demo locations
    const locationMap: Record<string, Coordinates> = {
      'Dublin 1': { latitude: 53.3498, longitude: -6.2603 },
      'Dublin 2': { latitude: 53.3381, longitude: -6.2592 },
      'Dublin 4': { latitude: 53.3321, longitude: -6.2419 },
      'Dublin 6': { latitude: 53.3147, longitude: -6.2622 },
      'Dublin 8': { latitude: 53.3439, longitude: -6.2924 }
    };

    return locationMap[location] || { latitude: 53.3498, longitude: -6.2603 };
  }
}

export const geolocationService = new GeolocationService();